import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Shield,
  Lock,
  Eye,
  Cookie,
  Globe,
  Mail,
  Users,
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const PrivacyPolicy = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
    Aos.init({ duration: 500, once: true });
  }, []);

  useSEO({
    title: "Privacy Policy | Game Zone Hub",
    description: "Read the Privacy Policy of Game Zone Hub. Learn how we protect your data while you play free online games securely.",
    keywords: "privacy policy, game zone hub privacy, online games privacy, safe browser games",
    canonical: "https://www.gamezonehub.in/privacy-policy",
  });

  const sections = [
    {
      id: "intro",
      title: "1. Introduction",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <p>
          Welcome to <strong>Game Zone Hub</strong>. We respect your privacy while you <strong>play free online games</strong> and are committed to protecting your personal data. 
          This privacy policy will inform you as to how we look after your personal data when you visit our website and tell you about your privacy rights.
        </p>
      ),
    },
    {
      id: "data-collection",
      title: "2. Information We Collect",
      icon: <Eye className="w-5 h-5" />,
      content: (
        <div className="space-y-2">
          <p>
            We do not collect personal information like your name, address, or phone number unless you voluntarily provide it (e.g., via Contact Form). 
            However, to improve our <strong>online games</strong> experience, we automatically collect certain non-personal data such as:
          </p>
          <ul className="list-disc pl-5 mt-2 text-gray-400">
              <li>Browser type and version</li>
              <li>Time zone setting and location</li>
              <li>Operating system and platform</li>
          </ul>
        </div>
      ),
    },
    {
      id: "adsense",
      title: "3. Google AdSense & Cookies",
      icon: <Cookie className="w-5 h-5" />,
      content: (
        <>
          <p className="mb-3">
            <strong>Game Zone Hub</strong> uses <strong>Google AdSense</strong> to display ads. Google uses cookies to serve ads based on your prior visits to our website or other websites.
          </p>
          <p>
            Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to our site and/or other sites on the Internet. 
            You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" className="text-blue-400 underline">Google Ads Settings</a>.
          </p>
        </>
      ),
    },
    {
      id: "security",
      title: "4. Data Security",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <p>
          We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way. 
          However, no method of transmission over the Internet is 100% secure.
        </p>
      ),
    },
    {
      id: "third-party",
      title: "5. Third-Party Links",
      icon: <Globe className="w-5 h-5" />,
      content: (
        <p>
          Our website may include links to third-party websites, plug-ins, and applications. 
          Clicking on those links may allow third parties to collect or share data about you. We do not control these third-party websites.
        </p>
      ),
    },
    {
      id: "children", 
      title: "6. Children's Information",
      icon: <Users className="w-5 h-5" />,
      content: (
        <p>
          Another part of our priority is adding protection for children while using the internet. We encourage parents and guardians to observe, participate in, and/or monitor and guide their online activity.
          <strong>Game Zone Hub</strong> does not knowingly collect any Personal Identifiable Information from children under the age of 13 who play our <strong>free browser games</strong>.
        </p>
      ),
    },
    {
      id: "contact",
      title: "7. Contact Us",
      icon: <Mail className="w-5 h-5" />,
      content: (
        <p>
          If you have any questions about this privacy policy, please contact us at: <br />
          <span className="text-blue-400 font-semibold">support@gamezonehub.in</span>
        </p>
      ),
    },
  ];

  return (
    <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3">
            Privacy <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Policy</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Your privacy is important to us.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          
          <div className="lg:w-1/3">
            <div className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg">
              <h3 className="text-white font-bold text-xl mb-4">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section, i) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500 group-hover:scale-110 transition-transform">{section.icon}</span>
                    <span className="text-sm font-medium">{section.title.split(". ")[1]}</span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                data-aos-delay={i * 120}
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 hover:border-blue-500/30 transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-400">{section.icon}</div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">{section.content}</div>
              </div>
            ))}
          </div>

        </div>
      </div>

    </div>
  );
};

export default PrivacyPolicy;